import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { differenceInCalendarDays, format } from "date-fns";
import { Calendar as CalendarIcon, Users, Baby, Tag, ChevronRight, Moon, ChevronDown } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { openBooking, sydneyToday } from "../lib/booking";
import { toast } from "sonner";

// Professional calendar dropdown per Check-in / Check-out.
// Single unified date field; clicking opens a compact styled calendar
// (react-day-picker) that stays inside the widget/modal and hands off
// exact dates to Little Hotelier.

const inp = "w-full bg-transparent text-[#0B192C] text-base font-serif outline-none";
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function CalendarField({ label, value, minDate, onChange, testid, align = "left" }) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });

  // Recompute position when open (and on scroll/resize).
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const panelW = 320; // approx calendar width
      const gap = 8;
      let left = align === "right" ? r.right - panelW : r.left;
      // Clamp horizontally to viewport with 12px padding.
      const maxLeft = window.innerWidth - panelW - 12;
      left = Math.max(12, Math.min(left, maxLeft));
      const spaceBelow = window.innerHeight - r.bottom;
      const panelH = 340;
      const openUp = spaceBelow < panelH + gap && r.top > panelH + gap;
      const top = openUp ? r.top - panelH - gap : r.bottom + gap;
      setPos({ top, left, width: panelW });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, align]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const displayText = value ? format(value, "EEE, d MMM yyyy") : "Select date";
  const labelId = `${testid}-label`;
  const defaultMonth = value || minDate || sydneyToday();

  const handleSelect = (d) => {
    if (!d) return;
    onChange?.(d);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-0" data-testid={testid} ref={rootRef}>
      <span id={labelId} className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] flex items-center gap-1.5">
        <CalendarIcon size={11} /> {label}
      </span>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-labelledby={labelId}
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
        className={`w-full h-11 flex items-center justify-between gap-3 rounded-xl border transition-colors duration-200 px-3.5 text-left font-serif ${value ? "border-[#D4AF37] bg-white text-[#0B192C]" : "border-[#0B192C]/15 bg-white/70 text-[#0B192C]/60"} hover:border-[#D4AF37]`}
      >
        <span className="truncate text-[15px]">{displayText}</span>
        <ChevronDown size={16} className={`text-[#0B192C]/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={`${label} calendar`}
          data-testid={`${testid}-panel`}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 2147483000, pointerEvents: "auto" }}
          className="rounded-xl border border-[#D4AF37]/40 bg-white shadow-2xl p-2 bfmi-cal"
        >
          <DayPicker
            mode="single"
            selected={value || undefined}
            onSelect={handleSelect}
            defaultMonth={defaultMonth}
            disabled={minDate ? { before: startOfDay(minDate) } : undefined}
            weekStartsOn={1}
            showOutsideDays
            captionLayout="dropdown"
            fromYear={sydneyToday().getFullYear()}
            toYear={sydneyToday().getFullYear() + 2}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default function BookingWidget({ variant = "float", onSubmitted }) {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [promo, setPromo] = useState("");

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.max(0, differenceInCalendarDays(checkOut, checkIn));
  }, [checkIn, checkOut]);

  // If check-out is now on/before check-in, clear it.
  useEffect(() => {
    if (checkIn && checkOut && checkOut <= checkIn) setCheckOut(null);
  }, [checkIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    if (!checkIn || !checkOut) { toast.error("Please choose your check-in and check-out dates."); return; }
    if (checkOut <= checkIn) { toast.error("Check-out must be after check-in."); return; }
    toast.success("Opening live availability on our secure booking engine…");
    openBooking({ checkIn, checkOut, adults, children, rooms: 1, promo });
    onSubmitted?.();
  };

  const checkOutMin = checkIn ? new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate() + 1) : sydneyToday();

  return (
    <div data-testid="booking-widget" className="relative glass soft-shadow rounded-2xl px-4 py-4 md:px-5 md:py-5 border border-[#D4AF37]/30">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2">
          <CalendarField label="Check In" value={checkIn} minDate={sydneyToday()}
            onChange={setCheckIn} testid="field-checkin" align="left" />
        </div>
        <div className="md:col-span-2">
          <CalendarField label="Check Out" value={checkOut} minDate={checkOutMin}
            onChange={setCheckOut} testid="field-checkout" align="right" />
        </div>

        <div className="grid grid-cols-3 md:grid-cols-3 md:col-span-2 gap-3 items-end">
          <div className="flex flex-col gap-1.5" data-testid="field-adults">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] flex items-center gap-1.5"><Users size={11} /> Adults</span>
            <input type="number" min={1} max={12} value={adults}
              onChange={(e) => setAdults(Math.max(1, +e.target.value))} className={inp} data-testid="adults-input" />
          </div>
          <div className="flex flex-col gap-1.5" data-testid="field-children">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] flex items-center gap-1.5"><Baby size={11} /> Children</span>
            <input type="number" min={0} max={12} value={children}
              onChange={(e) => setChildren(Math.max(0, +e.target.value))} className={inp} data-testid="children-input" />
          </div>
          <div className="flex flex-col gap-1.5" data-testid="field-promo">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] flex items-center gap-1.5"><Tag size={11} /> Promo</span>
            <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Optional"
              className={`${inp} placeholder:text-[#0B192C]/40`} data-testid="promo-input" />
          </div>
        </div>

        <div className="md:col-span-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
          {nights > 0 ? (
            <p className="text-sm text-[#0B192C] flex items-center gap-2" data-testid="booking-summary">
              <Moon size={13} className="text-[#D4AF37]" />
              <span><b className="font-serif text-base mr-1">{nights}</b>{nights === 1 ? "night" : "nights"} — best direct rate on the next screen.</span>
            </p>
          ) : (
            <p className="text-xs text-[#0B192C]/50">Pick your check-in and check-out dates to continue.</p>
          )}
          <button onClick={submit} data-testid="check-availability-btn"
            className="h-11 group bg-[#0B192C] hover:bg-[#D4AF37] text-white hover:text-[#0B192C] rounded-xl px-6 font-medium tracking-wide flex items-center justify-center gap-2 transition-colors duration-300 min-w-[160px]">
            Check Availability <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}
